/**
 * The instanced asteroid field (plan G7, contract ASTEROID_ATTR in render/contracts.ts).
 *
 * One `InstancedMesh` per (archetype, LOD): a CLONE of `kit.geometries[a][l]` carrying the `aSeed` (float) and
 * `aSpin` (vec4: instance-local unit axis, rad/s) instanced attributes, with `kit.materials[a]`, and capacity
 * for every rock of that archetype. Instance matrices are field-local (translation relative to
 * `layout.origin`: float32 keeps ~1 mm at 10 km), so the caller only moves `root` to the origin's render-space
 * position each frame (D-011).
 *
 * LOD bucketing (`update`) runs when the camera has moved `rebucketMeters` since the last bucketing or every
 * `rebucketFrames` frames: each rock picks its level from its centre distance (lod.ts, with hysteresis), and
 * only when a level changed are the buckets rewritten, into the preallocated arrays, with `count`, visibility
 * and a spin-safe bounding sphere per bucket (so three's frustum culling can skip whole buckets).
 * Rocks whose stable id (`layout.id`) is in the `removed` set (M2: `sim.removedRocks`, depleted or fractured) are
 * skipped by the bucketing and never drawn; `rebucket(cam, true)` rewrites the buckets at once after that set
 * changed. `update` / `rebucket` are allocation-free (D-014); construction allocates.
 *
 * Rock tint (MC-a6b, tint.ts): when `config.tint` is not neutral, every bucket also carries three's `instanceColor`
 * (no new mesh, draw call or material), copied per rock from `tints` (3 floats per layout index, the owner fills it
 * and calls `rebucket(cam, true)`). Neutral: no instance colour at all and `tints` is null.
 *
 * Shadow casters (PF-2, perf pass): with `casters` on, one more `InstancedMesh` per archetype at the kit's COARSEST
 * LOD, with the archetype's material and the buckets' instanced attributes (so the depth pipeline is the buckets'
 * own), sits on the shadow-only layer (`SHADOW_CASTER`): only the shadow levels draw it. It holds every drawn
 * (not culled, not removed) rock within `setCasterReach`'s range, rewritten on the bucketing cadence only when the
 * set changed, with a tight bounding sphere. The owner turns the buckets' `castShadow` off (`useCasterShadows`).
 *
 * Near casters (PF-2b): with `near` on as well, one more `InstancedMesh` per (archetype, LOD finer than the coarsest),
 * again with the buckets' material and instanced attributes, sits on `SHADOW_NEAR`, which only shadow levels 0-1 draw.
 * It holds every drawn rock whose sphere touches the live level-1 box (`setNearBox`, widened by the coarse set's
 * slack) at the rock's visible LOD, so close rocks keep their crater-rim shadows at the terminator; a rock at the
 * coarsest LOD is already exact in the coarse set. Rewritten on the bucketing cadence only when the set changed.
 */
import { Group, InstancedBufferAttribute, InstancedMesh, Matrix4, Quaternion, Sphere, Vector3, type BufferGeometry, type Material } from 'three/webgpu'
import type { Vec3 } from '../../core/math'
import { createRng, nextFloat } from '../../core/rng'
import { FIELD_ARCHETYPES } from '../../data/schemas'
import { ASTEROID_ATTR } from '../../render/contracts'
import { SHADOW_CASTER, SHADOW_NEAR } from '../../render/layers'
import type { AsteroidKit, ShadowBox } from '../../render/environment'
import type { FieldLayout } from '../../world/field'
import type { FieldViewConfig } from './config'
import { CULLED_NONE, pickLodLevel } from './lod'
import { tintEnabled } from './tint'
import { attributePathCapacity, DEFAULT_UNIFORM_BUFFER_LIMIT, flaggedUsage } from '../../render/uploads'

export interface FieldView {
  /** Parent of every field mesh; the caller places it at the layout origin in render space. */
  readonly root: Group
  readonly layout: FieldLayout
  /** `[layout archetype 0-5][lod]`. */
  readonly meshes: readonly (readonly InstancedMesh[])[]
  /** Level per rock after the last bucketing (0..lodCount-1 drawn, lodCount = culled, -1 = not yet bucketed). */
  readonly levels: Int8Array
  readonly lodCount: number
  /**
   * Instance colour per layout rock (3 floats, linear multipliers, identity 1), copied into the buckets on every
   * bucketing; null when the tint is neutral (no instance colour). The owner writes it, then `rebucket(cam, true)`.
   */
  readonly tints: Float32Array | null
  /** Rocks not drawn after the last bucketing (farther than the cull distance). */
  readonly culled: number
  /** Rocks not drawn after the last bucketing because their id is in the `removed` set. */
  readonly removed: number
  /** PF-2: the shadow-only caster mesh per layout archetype (empty when casters are off). */
  readonly casters: readonly InstancedMesh[]
  /** Rocks in the caster set after the last caster write. */
  readonly casterCount: number
  /**
   * PF-2: the live shadow reach (m from the camera position passed to `update`; `Environment.shadowReach`). A rock
   * casts while its distance minus its radius is within reach + `rebucketMeters` + `shadowCasters.padM`; a change
   * of more than the pad forces the next `update` to rebucket. Default: infinite. Allocation-free.
   */
  setCasterReach(reach: number): void
  /** PF-2b: the near caster mesh per (layout archetype, LOD 0..lodCount-2), row-major (empty when the near set is off). */
  readonly nearCasters: readonly InstancedMesh[]
  /** Rocks in the near set after the last near write. */
  readonly nearCount: number
  /**
   * PF-2b: the live level-1 shadow box (render space; `Environment.shadowBox`), or null when no level is active (then
   * every drawn rock finer than the coarsest LOD is near). A half-extent change of more than `shadowCasters.padM` (a
   * new hull) forces the next `update` to rebucket. Copies the numbers; allocation-free.
   */
  setNearBox(box: Readonly<ShadowBox> | null): void
  /** Once per frame with the float64 camera world position. Returns true when a bucketing ran. */
  update(cam: Readonly<Vec3>): boolean
  /**
   * Buckets now. Returns true when the instance data was rewritten: a level changed, or `force` (M2: the removed
   * set changed).
   */
  rebucket(cam: Readonly<Vec3>, force?: boolean): boolean
  /** Detaches the meshes and disposes the geometry clones (the kit's geometries and materials stay). */
  dispose(): void
}

interface Bucket {
  readonly mesh: InstancedMesh
  readonly matrices: Float32Array
  readonly seeds: Float32Array
  readonly spins: Float32Array
  readonly seedAttr: InstancedBufferAttribute
  readonly spinAttr: InstancedBufferAttribute
  /** The bucket's `instanceColor` array (null: the tint is neutral). */
  readonly colors: Float32Array | null
  /** Distance from the geometry origin to its farthest bounding-sphere point (mean radius 1 m): spin-safe. */
  readonly reach: number
}

/** Matrices, spins and a random orientation per rock, computed once per layout. */
function rockMatrices(layout: FieldLayout): Float32Array {
  const out = new Float32Array(layout.count * 16)
  const m = new Matrix4()
  const t = new Vector3()
  const q = new Quaternion()
  const s = new Vector3()
  const o = layout.origin
  for (let i = 0; i < layout.count; i++) {
    // A uniform random orientation (Shoemake) seeded by the rock's own seed, so a rock looks the same on
    // every preset (presets nest with identical per-rock values).
    const rng = createRng(Math.floor(layout.seed[i] * 4294967296) ^ 0x5bd1e995)
    const u1 = nextFloat(rng)
    const u2 = nextFloat(rng) * Math.PI * 2
    const u3 = nextFloat(rng) * Math.PI * 2
    const a = Math.sqrt(1 - u1)
    const b = Math.sqrt(u1)
    q.set(a * Math.sin(u2), a * Math.cos(u2), b * Math.sin(u3), b * Math.cos(u3))
    t.set(layout.x[i] - o.x, layout.y[i] - o.y, layout.z[i] - o.z)
    const r = layout.radius[i]
    s.set(r, r, r)
    m.compose(t, q, s)
    m.toArray(out, i * 16)
  }
  return out
}

function createBucket(
  geometry: BufferGeometry,
  material: Material,
  capacity: number,
  name: string,
  tinted: boolean,
  uniformLimit: number,
): Bucket {
  // A zero-sized GPU buffer is invalid (count stays 0); FG-1 raises small buckets onto the version-gated attribute path.
  const cap = attributePathCapacity(Math.max(1, capacity), uniformLimit)
  const clone = geometry.clone()
  const seeds = new Float32Array(cap)
  const spins = new Float32Array(cap * 4)
  const seedAttr = new InstancedBufferAttribute(seeds, 1)
  const spinAttr = new InstancedBufferAttribute(spins, 4)
  seedAttr.setUsage(flaggedUsage())
  spinAttr.setUsage(flaggedUsage())
  clone.setAttribute(ASTEROID_ATTR.seed, seedAttr)
  clone.setAttribute(ASTEROID_ATTR.spin, spinAttr)
  if (!clone.boundingSphere) clone.computeBoundingSphere()
  const gs = clone.boundingSphere
  const reach = gs ? gs.center.length() + gs.radius : 1
  const mesh = new InstancedMesh(clone, material, cap)
  mesh.name = name
  mesh.instanceMatrix.setUsage(flaggedUsage())
  mesh.count = 0
  mesh.visible = false
  mesh.boundingSphere = new Sphere()
  let colors: Float32Array | null = null
  if (tinted) {
    colors = new Float32Array(cap * 3).fill(1)
    const colorAttr = new InstancedBufferAttribute(colors, 3)
    colorAttr.setUsage(flaggedUsage())
    mesh.instanceColor = colorAttr
  }
  return { mesh, matrices: mesh.instanceMatrix.array as Float32Array, seeds, spins, seedAttr, spinAttr, colors, reach }
}

/**
 * `removed`: live set of stable rock ids not to draw (the owner mutates it; default none). `casters`: build the
 * PF-2 shadow caster set (the owner passes `config.shadowCasters.enabled[preset]`). `near`: also build the PF-2b near
 * set (`shadowCasters.nearSet[preset]`; only with `casters`).
 */
export function createFieldView(
  kit: AsteroidKit,
  layout: FieldLayout,
  config: FieldViewConfig,
  removed: ReadonlySet<number> = NONE_REMOVED,
  casters = false,
  near = false,
  uniformLimit = DEFAULT_UNIFORM_BUFFER_LIMIT,
): FieldView {
  const lodCount = kit.lodDistances.length
  if (lodCount < 1 || kit.geometries.length !== kit.archetypes.length) throw new Error('createFieldView: malformed AsteroidKit')
  if (lodCount > 126) throw new Error('createFieldView: too many LOD levels')

  // Thresholds per metre of radius: the kit's LOD switch-ins, then the cull distance.
  const thresholds = new Float64Array(lodCount + 1)
  for (let l = 0; l < lodCount; l++) thresholds[l] = kit.lodDistances[l]
  thresholds[lodCount] = config.cullDistancePerRadius > 0 ? config.cullDistancePerRadius : CULLED_NONE

  // Rocks per layout archetype (capacities).
  const perArchetype = new Int32Array(FIELD_ARCHETYPES.length)
  for (let i = 0; i < layout.count; i++) perArchetype[layout.archetype[i]]++

  const tinted = tintEnabled(config.tint)
  const root = new Group()
  root.name = 'field'
  const buckets: Bucket[] = []
  const meshes: InstancedMesh[][] = []
  for (let a = 0; a < FIELD_ARCHETYPES.length; a++) {
    const id = FIELD_ARCHETYPES[a]
    const k = kit.archetypes.indexOf(id)
    if (k < 0) throw new Error(`createFieldView: the AsteroidKit has no archetype ${id}`)
    if (kit.geometries[k].length !== lodCount) throw new Error(`createFieldView: archetype ${id} needs ${lodCount} LOD geometries`)
    const row: InstancedMesh[] = []
    for (let l = 0; l < lodCount; l++) {
      const b = createBucket(kit.geometries[k][l], kit.materials[k], perArchetype[a], `field:${id}:lod${l}`, tinted, uniformLimit)
      buckets.push(b)
      row.push(b.mesh)
      root.add(b.mesh)
    }
    meshes.push(row)
  }
  const casterBuckets: Bucket[] = []
  if (casters) {
    for (let a = 0; a < FIELD_ARCHETYPES.length; a++) {
      const id = FIELD_ARCHETYPES[a]
      const k = kit.archetypes.indexOf(id)
      const b = createBucket(kit.geometries[k][lodCount - 1], kit.materials[k], perArchetype[a], `field:${id}:caster`, tinted, uniformLimit)
      b.mesh.layers.set(SHADOW_CASTER)
      b.mesh.castShadow = true
      b.mesh.receiveShadow = false
      casterBuckets.push(b)
      root.add(b.mesh)
    }
  }
  const nearLods = casters && near ? lodCount - 1 : 0
  const nearBuckets: Bucket[] = []
  const rockReach = new Float64Array(FIELD_ARCHETYPES.length)
  for (let a = 0; a < FIELD_ARCHETYPES.length; a++) {
    for (let l = 0; l < lodCount; l++) rockReach[a] = Math.max(rockReach[a], buckets[a * lodCount + l].reach)
    const id = FIELD_ARCHETYPES[a]
    const k = kit.archetypes.indexOf(id)
    for (let l = 0; l < nearLods; l++) {
      const b = createBucket(kit.geometries[k][l], kit.materials[k], perArchetype[a], `field:${id}:near${l}`, tinted, uniformLimit)
      b.mesh.layers.set(SHADOW_NEAR)
      b.mesh.castShadow = true
      b.mesh.receiveShadow = false
      nearBuckets.push(b)
      root.add(b.mesh)
    }
  }

  const st: ViewState = {
    layout,
    buckets,
    lodCount,
    thresholds,
    matrices: rockMatrices(layout),
    levels: new Int8Array(layout.count).fill(-1),
    tints: tinted ? new Float32Array(layout.count * 3).fill(1) : null,
    bounds: new Float64Array(buckets.length * 6),
    hysteresis: config.hysteresis,
    moveSq: config.rebucketMeters * config.rebucketMeters,
    rebucketFrames: config.rebucketFrames,
    lastX: 0,
    lastY: 0,
    lastZ: 0,
    frames: 0,
    bucketed: false,
    culled: 0,
    removedSet: removed,
    removed: 0,
    casters: casterBuckets,
    inSet: new Uint8Array(casters ? layout.count : 0),
    casterBounds: new Float64Array(casterBuckets.length * 6),
    casterRange: Number.POSITIVE_INFINITY,
    casterSlack: config.rebucketMeters + config.shadowCasters.padM,
    casterPad: config.shadowCasters.padM,
    casterShrink: config.shadowCasters.shrink,
    casterCount: 0,
    casterDirty: casters,
    near: nearBuckets,
    nearLods,
    nearSlot: new Uint8Array(nearLods > 0 ? layout.count : 0),
    nearBounds: new Float64Array(nearBuckets.length * 6),
    rockReach,
    nearOn: false,
    nearCx: 0,
    nearCy: 0,
    nearCz: 0,
    nearLx: 0,
    nearLy: 1,
    nearLz: 0,
    nearHalf: 0,
    nearMargin: 0,
    nearCount: 0,
    nearDirty: nearLods > 0,
  }

  return {
    root,
    layout,
    meshes,
    levels: st.levels,
    lodCount,
    tints: st.tints,
    get culled(): number {
      return st.culled
    },
    get removed(): number {
      return st.removed
    },
    casters: casterBuckets.map((b) => b.mesh),
    get casterCount(): number {
      return st.casterCount
    },
    setCasterReach: (reach: number): void => setCasterReach(st, reach),
    nearCasters: nearBuckets.map((b) => b.mesh),
    get nearCount(): number {
      return st.nearCount
    },
    setNearBox: (box: Readonly<ShadowBox> | null): void => setNearBox(st, box),
    update: (cam: Readonly<Vec3>): boolean => updateView(st, cam),
    rebucket: (cam: Readonly<Vec3>, force = false): boolean => rebucketView(st, cam, force),
    dispose(): void {
      for (let b = 0; b < buckets.length; b++) {
        root.remove(buckets[b].mesh)
        buckets[b].mesh.geometry.dispose()
      }
      for (let b = 0; b < casterBuckets.length; b++) {
        root.remove(casterBuckets[b].mesh)
        casterBuckets[b].mesh.geometry.dispose()
      }
      for (let b = 0; b < nearBuckets.length; b++) {
        root.remove(nearBuckets[b].mesh)
        nearBuckets[b].mesh.geometry.dispose()
      }
    },
  }
}

/** Mutable internals of one view. The hot functions below take it explicitly (no per-frame closures). */
interface ViewState {
  readonly layout: FieldLayout
  readonly buckets: readonly Bucket[]
  readonly lodCount: number
  readonly thresholds: Float64Array
  /** Field-local instance matrix per rock (16 floats). */
  readonly matrices: Float32Array
  readonly levels: Int8Array
  /** Instance colour per rock (3 floats), or null (neutral tint). */
  readonly tints: Float32Array | null
  /** Per-bucket bounds scratch: min xyz, max xyz. */
  readonly bounds: Float64Array
  readonly hysteresis: number
  readonly moveSq: number
  readonly rebucketFrames: number
  lastX: number
  lastY: number
  lastZ: number
  frames: number
  bucketed: boolean
  culled: number
  /** Stable ids not to draw (live: the owner mutates it). */
  readonly removedSet: ReadonlySet<number>
  removed: number
  /** PF-2 caster meshes per archetype (empty: casters off). */
  readonly casters: readonly Bucket[]
  /** Per rock: 0 = not in the caster set at the last bucketing, else its archetype + 1 (its caster mesh + 1). */
  readonly inSet: Uint8Array
  /** Per-caster bounds scratch: min xyz, max xyz. */
  readonly casterBounds: Float64Array
  /** Range (m, rock surface from the camera) of the caster set: reach + slack. */
  casterRange: number
  /** `rebucketMeters` + `padM`. */
  readonly casterSlack: number
  readonly casterPad: number
  readonly casterShrink: number
  casterCount: number
  /** The range moved by more than the pad (or the set was never written): rebucket at the next `update`. */
  casterDirty: boolean
  /** PF-2b near caster meshes, index archetype × nearLods + LOD (empty: the near set is off). */
  readonly near: readonly Bucket[]
  /** LODs with a near mesh (lodCount - 1; 0 = the near set is off). */
  readonly nearLods: number
  /** Per rock: 0 = not in the near set, else its near mesh index + 1. */
  readonly nearSlot: Uint8Array
  /** Per-near-mesh bounds scratch: min xyz, max xyz. */
  readonly nearBounds: Float64Array
  /** Per archetype: the largest geometry reach over its LODs (m per metre of radius). */
  readonly rockReach: Float64Array
  /** A level-1 box is set (false: no active level, no box culling). */
  nearOn: boolean
  nearCx: number
  nearCy: number
  nearCz: number
  nearLx: number
  nearLy: number
  nearLz: number
  nearHalf: number
  nearMargin: number
  nearCount: number
  /** The box appeared, went or changed size by more than the pad (or was never written): rebucket at the next `update`. */
  nearDirty: boolean
}

const NONE_REMOVED: ReadonlySet<number> = new Set()

/** True when rock `i` is in the removed set. Allocation-free. */
function isRemoved(st: ViewState, i: number): boolean {
  return st.removedSet.size > 0 && st.removedSet.has(st.layout.id[i])
}

function updateView(st: ViewState, cam: Readonly<Vec3>): boolean {
  st.frames++
  const dx = cam.x - st.lastX
  const dy = cam.y - st.lastY
  const dz = cam.z - st.lastZ
  if (st.bucketed && !st.casterDirty && !st.nearDirty && st.frames < st.rebucketFrames && dx * dx + dy * dy + dz * dz <= st.moveSq) return false
  rebucketView(st, cam, false)
  return true
}

function rebucketView(st: ViewState, cam: Readonly<Vec3>, force: boolean): boolean {
  st.lastX = cam.x
  st.lastY = cam.y
  st.lastZ = cam.z
  st.frames = 0
  st.bucketed = true
  const layout = st.layout
  const levels = st.levels
  const inSet = st.inSet
  const casting = st.casters.length > 0
  let changed = false
  let castChanged = st.casterDirty
  st.casterDirty = false
  const nearSlot = st.nearSlot
  const nearing = st.nearLods > 0
  let nearChanged = st.nearDirty
  st.nearDirty = false
  for (let i = 0; i < layout.count; i++) {
    // A removed rock keeps its last level (it is never drawn again), so it cannot trigger rewrites.
    if (isRemoved(st, i)) continue
    const dx = layout.x[i] - cam.x
    const dy = layout.y[i] - cam.y
    const dz = layout.z[i] - cam.z
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
    const l = pickLodLevel(d, layout.radius[i], st.thresholds, levels[i], st.hysteresis)
    if (l !== levels[i]) {
      levels[i] = l
      changed = true
    }
    if (casting) {
      const c = l < st.lodCount && d - layout.radius[i] <= st.casterRange ? layout.archetype[i] + 1 : 0
      if (c !== inSet[i]) {
        inSet[i] = c
        castChanged = true
      }
    }
    if (nearing) {
      const a = layout.archetype[i]
      const n = l < st.nearLods && touchesNearBox(st, dx, dy, dz, layout.radius[i] * st.rockReach[a]) ? a * st.nearLods + l + 1 : 0
      if (n !== nearSlot[i]) {
        nearSlot[i] = n
        nearChanged = true
      }
    }
  }
  if (changed || force) writeBuckets(st)
  if (casting && (castChanged || force)) writeCasters(st)
  if (nearing && (nearChanged || force)) writeNear(st)
  return changed || force || (casting && castChanged) || (nearing && nearChanged)
}

/** PF-2b: the level-1 box from the environment; appearing, going or a half-extent move beyond the pad rebuckets. */
function setNearBox(st: ViewState, box: Readonly<ShadowBox> | null): void {
  if (st.nearLods === 0) return
  if (box === null) {
    if (st.nearOn) st.nearDirty = true
    st.nearOn = false
    return
  }
  if (!st.nearOn || Math.abs(box.half - st.nearHalf) > st.casterPad) st.nearDirty = true
  st.nearOn = true
  st.nearCx = box.cx
  st.nearCy = box.cy
  st.nearCz = box.cz
  st.nearLx = box.lx
  st.nearLy = box.ly
  st.nearLz = box.lz
  st.nearHalf = box.half
  st.nearMargin = box.margin
}

/**
 * PF-2b: true when a rock at (x, y, z) (render space, relative to the camera) with reach `r` may touch the level-1
 * box: the box's circumscribing cylinder about the light axis (radius half·√2, from half behind the centre to
 * half + margin toward the light), widened by `r` and the coarse set's slack (`rebucketMeters` + `padM`) for the
 * motion between bucketings. Always true without a box.
 */
function touchesNearBox(st: ViewState, x: number, y: number, z: number, r: number): boolean {
  if (!st.nearOn) return true
  const rx = x - st.nearCx
  const ry = y - st.nearCy
  const rz = z - st.nearCz
  const along = rx * st.nearLx + ry * st.nearLy + rz * st.nearLz
  const w = r + st.casterSlack
  const h = st.nearHalf
  if (along < -h - w || along > h + st.nearMargin + w) return false
  const lim = h * Math.SQRT2 + w
  return rx * rx + ry * ry + rz * rz - along * along <= lim * lim
}

/** PF-2: the caster range from the live shadow reach; a move of more than the pad forces a rebucket. */
function setCasterReach(st: ViewState, reach: number): void {
  const range = reach + st.casterSlack
  if (range === st.casterRange) return
  if (Math.abs(range - st.casterRange) <= st.casterPad) return
  st.casterRange = range
  st.casterDirty = true
}

/** PF-2: rewrites the coarse caster meshes from the caster set (shrunk about each rock's centre). */
function writeCasters(st: ViewState): void {
  st.casterCount = writeSet(st, st.casters, st.inSet, st.casterBounds, st.casterShrink)
}

/** PF-2b: rewrites the near caster meshes from the near set (each rock in its visible LOD's mesh, unshrunk). */
function writeNear(st: ViewState): void {
  st.nearCount = writeSet(st, st.near, st.nearSlot, st.nearBounds, 1)
}

/**
 * Rewrites the caster meshes `set` from `slot` (per rock: 0 = out, else its mesh index + 1): instance data (rotation
 * and scale × `k`), counts, tight bounds, visibility (empty meshes draw nothing) and uploads. Returns the rocks
 * written. Removed rocks are skipped. Allocation-free.
 */
function writeSet(st: ViewState, set: readonly Bucket[], slot: Uint8Array, bounds: Float64Array, k: number): number {
  const layout = st.layout
  const matrices = st.matrices
  for (let b = 0; b < set.length; b++) {
    set[b].mesh.count = 0
    const o = b * 6
    bounds[o] = bounds[o + 1] = bounds[o + 2] = Number.POSITIVE_INFINITY
    bounds[o + 3] = bounds[o + 4] = bounds[o + 5] = Number.NEGATIVE_INFINITY
  }
  let total = 0
  for (let i = 0; i < layout.count; i++) {
    if (slot[i] === 0 || isRemoved(st, i)) continue
    const m = slot[i] - 1
    const b = set[m]
    const n = b.mesh.count++
    total++
    const src = i * 16
    const dst = n * 16
    // The rotation-scale columns scale about the rock's centre (shrink); translation and the w row are copied.
    for (let e = 0; e < 16; e++) b.matrices[dst + e] = e < 12 && (e & 3) !== 3 ? matrices[src + e] * k : matrices[src + e]
    b.seeds[n] = layout.seed[i]
    b.spins[n * 4] = layout.spinAxis[i * 3]
    b.spins[n * 4 + 1] = layout.spinAxis[i * 3 + 1]
    b.spins[n * 4 + 2] = layout.spinAxis[i * 3 + 2]
    b.spins[n * 4 + 3] = layout.spinRate[i]
    const o = m * 6
    const x = matrices[src + 12]
    const y = matrices[src + 13]
    const z = matrices[src + 14]
    if (x < bounds[o]) bounds[o] = x
    if (y < bounds[o + 1]) bounds[o + 1] = y
    if (z < bounds[o + 2]) bounds[o + 2] = z
    if (x > bounds[o + 3]) bounds[o + 3] = x
    if (y > bounds[o + 4]) bounds[o + 4] = y
    if (z > bounds[o + 5]) bounds[o + 5] = z
  }
  for (let b = 0; b < set.length; b++) {
    const sphere = set[b].mesh.boundingSphere
    if (!sphere) continue
    const o = b * 6
    if (set[b].mesh.count === 0) sphere.center.set(0, 0, 0)
    else sphere.center.set((bounds[o] + bounds[o + 3]) * 0.5, (bounds[o + 1] + bounds[o + 4]) * 0.5, (bounds[o + 2] + bounds[o + 5]) * 0.5)
    sphere.radius = 0
  }
  for (let i = 0; i < layout.count; i++) {
    if (slot[i] === 0 || isRemoved(st, i)) continue
    const c = set[slot[i] - 1]
    const sphere = c.mesh.boundingSphere
    if (!sphere) continue
    const src = i * 16
    const dx = matrices[src + 12] - sphere.center.x
    const dy = matrices[src + 13] - sphere.center.y
    const dz = matrices[src + 14] - sphere.center.z
    const r = Math.sqrt(dx * dx + dy * dy + dz * dz) + layout.radius[i] * c.reach
    if (r > sphere.radius) sphere.radius = r
  }
  for (let b = 0; b < set.length; b++) {
    const c = set[b]
    c.mesh.visible = c.mesh.count > 0
    c.mesh.instanceMatrix.needsUpdate = true
    c.seedAttr.needsUpdate = true
    c.spinAttr.needsUpdate = true
  }
  return total
}

/**
 * PF-2: after `enableShadows(view.root)`, the drawn buckets stop casting when the view has casters (the caster set
 * casts instead). Not per frame.
 */
export function useCasterShadows(view: FieldView): void {
  if (view.casters.length === 0) return
  for (const row of view.meshes) for (const m of row) m.castShadow = false
  for (const c of view.casters) {
    c.castShadow = true
    c.receiveShadow = false
  }
  for (const c of view.nearCasters) {
    c.castShadow = true
    c.receiveShadow = false
  }
}

/** Rewrites every bucket from the rock levels: instance data, counts, bounds and upload flags. */
function writeBuckets(st: ViewState): void {
  const { layout, buckets, levels, matrices, bounds, lodCount, tints } = st
  for (let b = 0; b < buckets.length; b++) {
    buckets[b].mesh.count = 0
    const o = b * 6
    bounds[o] = bounds[o + 1] = bounds[o + 2] = Number.POSITIVE_INFINITY
    bounds[o + 3] = bounds[o + 4] = bounds[o + 5] = Number.NEGATIVE_INFINITY
  }
  let culled = 0
  let removed = 0
  for (let i = 0; i < layout.count; i++) {
    if (isRemoved(st, i)) {
      removed++
      continue
    }
    const l = levels[i]
    if (l >= lodCount) {
      culled++
      continue
    }
    const bi = layout.archetype[i] * lodCount + l
    const b = buckets[bi]
    const k = b.mesh.count++
    const src = i * 16
    const dst = k * 16
    for (let e = 0; e < 16; e++) b.matrices[dst + e] = matrices[src + e]
    b.seeds[k] = layout.seed[i]
    b.spins[k * 4] = layout.spinAxis[i * 3]
    b.spins[k * 4 + 1] = layout.spinAxis[i * 3 + 1]
    b.spins[k * 4 + 2] = layout.spinAxis[i * 3 + 2]
    b.spins[k * 4 + 3] = layout.spinRate[i]
    const colors = b.colors
    if (colors !== null && tints !== null) {
      colors[k * 3] = tints[i * 3]
      colors[k * 3 + 1] = tints[i * 3 + 1]
      colors[k * 3 + 2] = tints[i * 3 + 2]
    }
    const x = matrices[src + 12]
    const y = matrices[src + 13]
    const z = matrices[src + 14]
    const o = bi * 6
    if (x < bounds[o]) bounds[o] = x
    if (y < bounds[o + 1]) bounds[o + 1] = y
    if (z < bounds[o + 2]) bounds[o + 2] = z
    if (x > bounds[o + 3]) bounds[o + 3] = x
    if (y > bounds[o + 4]) bounds[o + 4] = y
    if (z > bounds[o + 5]) bounds[o + 5] = z
  }
  st.culled = culled
  st.removed = removed

  // Bounding spheres: centred on each bucket's box, reaching every rock's farthest point.
  for (let b = 0; b < buckets.length; b++) {
    const bucket = buckets[b]
    const o = b * 6
    const sphere = bucket.mesh.boundingSphere
    if (!sphere) continue
    if (bucket.mesh.count === 0) sphere.center.set(0, 0, 0)
    else sphere.center.set((bounds[o] + bounds[o + 3]) * 0.5, (bounds[o + 1] + bounds[o + 4]) * 0.5, (bounds[o + 2] + bounds[o + 5]) * 0.5)
    sphere.radius = 0
  }
  for (let i = 0; i < layout.count; i++) {
    const l = levels[i]
    if (l >= lodCount || isRemoved(st, i)) continue
    const bucket = buckets[layout.archetype[i] * lodCount + l]
    const sphere = bucket.mesh.boundingSphere
    if (!sphere) continue
    const src = i * 16
    const dx = matrices[src + 12] - sphere.center.x
    const dy = matrices[src + 13] - sphere.center.y
    const dz = matrices[src + 14] - sphere.center.z
    const r = Math.sqrt(dx * dx + dy * dy + dz * dz) + layout.radius[i] * bucket.reach
    if (r > sphere.radius) sphere.radius = r
  }

  for (let b = 0; b < buckets.length; b++) {
    const bucket = buckets[b]
    bucket.mesh.visible = bucket.mesh.count > 0
    bucket.mesh.instanceMatrix.needsUpdate = true
    bucket.seedAttr.needsUpdate = true
    bucket.spinAttr.needsUpdate = true
    const colorAttr = bucket.mesh.instanceColor
    if (colorAttr !== null) colorAttr.needsUpdate = true
  }
}
